import * as React from "react"
import { navigate } from "gatsby"
import { ThemeCss, Heading, Text, GitHubIcon, Link } from "gatsby-interface"
import StarterCard from "@modules/site/create/Provision/components/StarterCard"
import { siteCardContainerCss } from "@modules/site/create/SiteCreationTypePicker"
import { MdLaunch } from "react-icons/md"
import { cmsVendorIcons } from "../../icons/index"
import defaultStarterImg from "../../assets/default-starter.png"
import contentfulStarterImg from "../../assets/homepage-starter-contentful.png"
import { useTracker, SegmentEventType } from "@modules/analytics"

type StarterProps = {
  title: string
  repoUrl: string
  image: string
  icon?: JSX.Element
}

// styles
const paragraphCss: ThemeCss = theme => ({
  display: "flex",
  alignItems: "center",
  color: theme.colors.grey[60],
  gap: theme.space[2],
  fontSize: theme.fontSizes[0],
})

const listItemCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[0],
  margin: theme.space[0],
  background: theme.colors.white,
  width: `100%`,
  height: `100%`,
  borderRadius: theme.space[2],
  boxShadow: `0px 2px 4px rgba(71, 63, 79, 0.08), 0px 1px 2px rgba(46, 41, 51, 0.08)`,
  "&:hover": {
    boxShadow: `none`,
  },
})

const listCss: ThemeCss = theme => ({
  display: `grid`,
  gridTemplateColumns: `1fr`,
  margin: theme.space[0],
  marginTop: theme.space[7],
  paddingTop: theme.space[3],
  paddingBottom: theme.space[7],
  gridGap: theme.space[5],
  listStyle: `none`,
  [theme.mediaQueries.phablet]: {
    gridTemplateColumns: `repeat(2, 1fr)`,
  },
  [`@media (min-width: 1100px)`]: {
    // prevents unwanted wrap around - needs custom breakpoint
    height: `fit-content`,
    maxHeight: `100%`,
    overflowY: `auto`,
  },
  [theme.mediaQueries.hd]: {
    gridTemplateColumns: `repeat(2, minmax(175px, 1fr))`,
  },
})

export default function StarterPicker() {
  const { trackSegment } = useTracker()

  const starters = [
    {
      title: "Contentful Homepage",
      repoUrl: "https://github.com/gatsbyjs/gatsby-starter-contentful-homepage",
      image: contentfulStarterImg,
      icon: cmsVendorIcons["CONTENTFUL"],
    },
    {
      title: "Gatsby Default Starter",
      repoUrl: "https://github.com/gatsbyjs/gatsby-starter-default",
      image: defaultStarterImg,
    },
  ]

  return (
    <div
      css={theme => [
        siteCardContainerCss(theme),
        {
          boxShadow: `none`,
          background: theme.colors.grey[5],
        },
      ]}
    >
      <div css={{ display: `flex`, justifyContent: `space-between` }}>
        <Heading as="h2" css={theme => ({ fontSize: theme.fontSizes[2] })}>
          Clone a Gatsby Starter
        </Heading>
        <Text tone="NEUTRAL" variant="PRIMARY" css={paragraphCss}>
          <GitHubIcon size="xxsmall" /> Requires Github
        </Text>
      </div>
      <Text tone="NEUTRAL" variant="PRIMARY">
        {
          "We'll create a repository in your Git Account and provision content in a headless CMS if needed."
        }
      </Text>
      <ul css={listCss}>
        {starters.map((starter: StarterProps) => {
          return (
            <li
              key={starter.repoUrl}
              css={listItemCss}
              aria-label={`a starter based on ${starter.title}`}
            >
              <StarterCard
                onHandleChoice={(): void | Promise<void> => {
                  trackSegment({
                    type: SegmentEventType.Track,
                    event: "Clone a Starter Add Site Clicked",
                    properties: {
                      title: starter.title,
                      repoUrl: starter.repoUrl,
                    },
                  })
                  const deployNowUrl = `/dashboard/deploynow?url=${starter.repoUrl}`
                  return navigate(`${deployNowUrl}`)
                }}
                title={starter.title}
                image={starter.image}
                icon={starter.icon}
              />
            </li>
          )
        })}
      </ul>
      <Text size="S" css={theme => ({ color: theme.colors.grey[50] })}>
        Find more Gatsby Starters in our{" "}
        <Link
          href="https://www.gatsbyjs.com/starters/"
          variant="SIMPLE"
          onClick={() =>
            trackSegment({
              type: SegmentEventType.Track,
              event: `Clone a Starter Starter Library Link Clicked`,
            })
          }
        >
          Starter Library
          <MdLaunch
            css={theme => ({
              marginLeft: theme.space[2],
            })}
          />
        </Link>
      </Text>
    </div>
  )
}
